import { Component, OnInit } from '@angular/core';
import * as moment from 'moment';
import {DataService, ResolveAssistanceType, ResolveCategory, ResolveLga, ResolvePracticeChangeActivity, ResolveRiskType} from '../../services/data.service';
import {ActionPlanType, AdditionalSupportType, AssistanceProvidedType, ExtensionCategory, Hort360PropertySummary, KnownUserType, LgaType, PracticeChangeActivity} from '../../../../functions/src/types';
import invert from 'invert-color';
import * as Highcharts from 'highcharts';
import {SeriesOptionsType} from 'highcharts';
import {ReportingService, ReportRange} from "../../services/reporting.service";
import {UserService} from "../../services/user.service";
import {RSDialogService} from "../../../libs/rsnotify/rsdialog.service";
import * as XLSX from 'xlsx';
import {PropertyService} from "../../services/property.service";
import {PracticeChangeService} from "../../services/practice-change.service";
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {RSLoadingModalService} from "../../../libs/rsloading/rsloading.service";
import {
  PracticeChangeForm,
  PracticeChangeFormPesticide,
  PracticeChangeFormSoil,
  PracticeChangeFormWater
} from "../../services/practice-change.types";
import {PracticeChangeFertiliserComponent} from "../practice-change-fertiliser/practice-change-fertiliser.component";

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit {
  dateStart = moment().subtract(7, 'days').toDate();
  dateEnd = moment().toDate();
  filterUser: KnownUserType | null = null;
  filterCategory: ExtensionCategory | null = null;
  filterAssistanceType: AssistanceProvidedType | null = null;
  filterPracticeChangeActivity: PracticeChangeActivity  | null = null;

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {
    chart: {
      type: 'column'
    },
    title: {
      text: null,
    },
    xAxis: {
      categories: []
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Interactions'
      },
      stackLabels: {
        enabled: true,
        style: {
          fontWeight: 'bold',
          color: ( // theme
            Highcharts.defaultOptions.title.style &&
            Highcharts.defaultOptions.title.style.color
          ) || 'gray'
        }
      }
    },
    tooltip: {
      headerFormat: '<b>{point.x}</b><br/>',
      pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}'
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        dataLabels: {
          enabled: true
        }
      }
    },
    series: []
  };
  reportLoading = true;
  reportResults: ReportRange;
  reportingType: ReportingType = ReportingType.INTERACTIONS_BY_CATEGORY;
  reportingTypes = Object.values(ReportingType).filter(v => !isNaN(Number(v)));
  reportingTypeObjects = ReportingType;
  reportingTypeHeaders = {
    [ReportingType.INTERACTIONS_BY_CATEGORY]: ['Interactions by Category', 'Interactions include event attendance and a sum of extension activities (i.e. 5 maps)'],
    [ReportingType.INTERACTIONS_PER_USER]: ['Interactions by User', 'Interactions include event attendance and a sum of extension activities (i.e. 5 maps)'],
    [ReportingType.CONTACT_EVENTS]: ['Property Contact + Event Attendance', 'Property correspondence through events or direct contact.'],
    [ReportingType.ALL_ACTIVITIES]: ['All Activities', 'Sum of all interactions, events & activities undertaken'],
    [ReportingType.MEETING_WITH_OTHERS]: ['Meeting with others', 'Meetings scheduled with other users.'],
    [ReportingType.ASSISTANCE_REPORT]: ['Assistance Provided', ' Assistance provided to other users.'],
    [ReportingType.PRACTICE_CHANGE_REPORT]: ['Practice Change', 'Practice change activities undertaken.'],
    [ReportingType.REEF_CERTIFICATE_REPORT]: ['Reef Certificate', 'Reef certification dates'],
  };

  constructor(
    public dataService: DataService,
    public reportingService: ReportingService,
    public userService: UserService,
    public resolveCategory: ResolveCategory,
    public resolveLga: ResolveLga,
    public resolveAssistanceType: ResolveAssistanceType,
    public resolvePracticeChangeActivity: ResolvePracticeChangeActivity,
    public resolveRiskType: ResolveRiskType,
    public rsDialogService: RSDialogService,
    public rsLoadingModalService: RSLoadingModalService,
    public propertyService: PropertyService,
    public practiceChangeService: PracticeChangeService,
    public fbDb: AngularFirestore
  ) { }

  ngOnInit() {
    // Load data;
    if (this.dataService.initialLoad) {
      this.dataService.initialLoad.then(v => this.loadReport());
    } else {
      this.loadReport();
    }
  }

  loadReport(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (this.dataService.initialLoad) {
        this.rsDialogService.showErrorDialog('Please wait a moment while the system loads. If this persists, please refresh!');
        reject();
        return;
      }
      (this.chartOptions.xAxis as any).categories = [];
      this.chartOptions.series = [];
      this.reportLoading = true;
      this.reportResults = null;
      const from = moment(this.dateStart).set('hour', 0).set('minute', 0).set('second', 0);
      const to = moment(this.dateEnd).set('hour', 23).set('minute', 59).set('second', 59);
      const days = to.diff(from, 'days', false);

      this.reportingService.get(from.format('X'), to.format('X'), this.filterUser?.uid).then(results => {
        const categories: {[key: string]: number[]} = {};

        if (this.reportingType === ReportingType.INTERACTIONS_BY_CATEGORY) {
          results.uniqueCategories.forEach(uc => {
            if (this.filterCategory?.id && this.filterCategory.id !== uc) {
              return;
            }
            categories[uc] = [];
          });
        } else if (this.reportingType === ReportingType.INTERACTIONS_PER_USER) {
          results.uniqueUsers.forEach(uc => {
            categories[uc] = [];
          });
        } else if (this.reportingType === ReportingType.ALL_ACTIVITIES) {
          results.activities.forEach(a => {
            categories[a.activityId] = [];
          });
        } else if (this.reportingType === ReportingType.MEETING_WITH_OTHERS) {
          results.uniqueUsersForMeetings.forEach(uc => {
            categories[uc] = [];
          });
        } else if (this.reportingType === ReportingType.ASSISTANCE_REPORT){
          results.uniqueAssistanceTypes.forEach(uc => {
            if(this.filterAssistanceType?.id && this.filterAssistanceType.id !== uc){
              return;
            }
            categories[uc] = [];
          })
        } else if (this.reportingType === ReportingType.PRACTICE_CHANGE_REPORT){
          results.uniquePracticeChangeActivities.forEach(uc => {
            if(this.filterPracticeChangeActivity?.id && this.filterPracticeChangeActivity.id !== uc){
              return;
            }
            categories[uc] = [];
          })
        }
        else if(this.reportingType === ReportingType.REEF_CERTIFICATE_REPORT){
          this.reportingType = ReportingType.REEF_CERTIFICATE_REPORT;
          categories.Attendees = [];
          categories.Contacts = [];
        }
        else {
          this.reportingType = ReportingType.CONTACT_EVENTS;
          categories.Attendees = [];
          categories.Contacts = [];
        }


        const labels = [];
        for (let d = 0; d <= days; d++) { // Include all?
          const dayStart = from.clone().add('days', d);
          const dayEnd = dayStart.clone().set('hour', 23).set('minute', 59).set('second', 59);
          const dailyResults = results.reportingFor(dayStart.format('X'), dayEnd.format('X'));

          let interactions:any;

          switch (this.reportingType) {
            case ReportingType.INTERACTIONS_BY_CATEGORY:
              interactions = dailyResults.interactionsPerCategory;
              break;
            case ReportingType.ALL_ACTIVITIES:
              interactions = dailyResults.interactionsByActivity;
              break;
            case ReportingType.MEETING_WITH_OTHERS:
              interactions = dailyResults.meetingsWithOthers;
              break;
            case ReportingType.ASSISTANCE_REPORT:
              interactions = dailyResults.assistanceProvidedPerType;
              break;
            case ReportingType.PRACTICE_CHANGE_REPORT:
              interactions = dailyResults.practiceChangesPerType;
              break;
            default:
              interactions = dailyResults.interactionsPerUser;
              break;
          }

          // How many?
          if (this.reportingType === ReportingType.INTERACTIONS_BY_CATEGORY ||
            this.reportingType === ReportingType.INTERACTIONS_PER_USER ||
            this.reportingType === ReportingType.ALL_ACTIVITIES ||
            this.reportingType === ReportingType.MEETING_WITH_OTHERS ||
            this.reportingType === ReportingType.ASSISTANCE_REPORT ||
            this.reportingType === ReportingType.PRACTICE_CHANGE_REPORT) {
            Object.keys(categories).forEach(cat => {
              let value = null;
              if (interactions.hasOwnProperty(cat)) {
                value = interactions[cat];
              }
              categories[cat].push(value);
            });
          } else {
            categories.Attendees.push(dailyResults.totalAttendance);
            categories.Contacts.push(dailyResults.contacts.length);
          }

          // Add label
          labels.push(dayStart.format('DD/MM'));
        }
        (this.chartOptions.xAxis as any).categories = labels;

        if (this.reportingType === ReportingType.INTERACTIONS_BY_CATEGORY) {
          this.chartOptions.series = Object.keys(categories).map(cat => {
            const categoryType = this.resolveCategory.transform(cat);
            return {
              name: categoryType.name,
              color: categoryType.color,
              data: categories[cat]
            } as SeriesOptionsType;
          });
        } else if (
          this.reportingType === ReportingType.INTERACTIONS_PER_USER ||
          this.reportingType === ReportingType.MEETING_WITH_OTHERS) {
          const knownUsers = this.dataService.knownUsers.getValue();
          this.chartOptions.series = Object.keys(categories).map(userId => {
            const userType = knownUsers.find(u => u.uid + '' === userId + '');
            return {
              name: userType?.name,
              data: categories[userId]
            } as SeriesOptionsType;
          });
        } else if (this.reportingType === ReportingType.ASSISTANCE_REPORT) {

          this.chartOptions.series = Object.keys(categories).map(cat => {
            const assistanceType = this.resolveAssistanceType.transform(cat);
            const categoryType = this.resolveCategory.transform(assistanceType.category);
            return {
              name: assistanceType.name,
              color: categoryType.color,
              data: categories[cat]
            } as SeriesOptionsType;
          });

        } else if (this.reportingType === ReportingType.PRACTICE_CHANGE_REPORT) {

          this.chartOptions.series = Object.keys(categories).map(cat => {
            const practiceChangeActivity = this.resolvePracticeChangeActivity.transform(cat);
            const categoryType = this.resolveCategory.transform(practiceChangeActivity.category);
            return {
              name: practiceChangeActivity.name,
              color: categoryType.color,
              data: categories[cat]
            } as SeriesOptionsType;
          });

        }
        else if (this.reportingType === ReportingType.ALL_ACTIVITIES) {
          const activities = this.dataService.activityTypes.getValue();
          const events = this.dataService.events.getValue();
          const categoryTypes = this.dataService.categories.getValue();

          this.chartOptions.series = Object.keys(categories).map(catId => {
            // catId === ActivityId || EventId || CategoryId (for PC)
            const activityType = activities.find(a => a.id === catId);
            const eventType = events.find(e => e.id === catId);
            const categoryType = categoryTypes.find(c => c.id === catId);

            const categoryId = activityType?.category || eventType?.category || categoryType?.id;
            if (this.filterCategory?.id && this.filterCategory.id !== categoryId) {
              return;
            }
            const category = categoryType || this.resolveCategory.transform(categoryId);

            let name = activityType?.name;
            if (!name && eventType?.name) {
              name = 'Event';
            } else if (!name) {
              name = 'Practice Change';
            }
            return {
              name: category.name + ' - ' + name,
              data: categories[catId],
              color: category.color
            } as SeriesOptionsType;
          }).filter(v => !!v);
        } else if(this.reportingType === ReportingType.REEF_CERTIFICATE_REPORT) {
          // No chart data, but valid for raw download
          this.chartOptions.series = [{
            name: 'Data available in raw download',
            data: []
          } as any];
        } else {
          this.reportingType = ReportingType.CONTACT_EVENTS;
          this.chartOptions.series = Object.keys(categories).map(type => {
            return {
              name: type,
              data: categories[type]
            } as SeriesOptionsType;
          });
        }
        this.reportResults = results;
        this.reportLoading = false;
        resolve();
      }, e => {
        console.error(e);
        this.rsDialogService.showErrorDialog('Something went wrong. ' + e.message);
        reject();
      }).catch(e => {
        console.error(e);
        this.rsDialogService.showErrorDialog('Something went wrong. ' + e.message);
        reject();
      });
    });
  }

  async downloadRaw() {
    /*try {
      await this.loadReport();
    } catch (e) {
      return;
    }*/
    // Allow REEF_CERTIFICATE_REPORT to proceed even if interactions are 0, as it fetches data independently
    if ((!this.reportResults || this.reportResults.totalInteractions === 0) && this.reportingType !== ReportingType.REEF_CERTIFICATE_REPORT) {
      this.rsDialogService.showErrorDialog('There is no data to report on.');
      return;
    }
    this.rsLoadingModalService.show();

    // Get Globals;
    const knownUsers = this.dataService.knownUsers.getValue();
    const activities = this.dataService.activityTypes.getValue();
    const states = this.dataService.globals.getValue().states;
    // Generate worksheet
    const wb: XLSX.WorkBook = XLSX.utils.book_new();


    if (this.reportingType === ReportingType.REEF_CERTIFICATE_REPORT) {
      // Reef Certificates
      const loadReefCertificates = new Promise<void>(async (resolve, reject) => {
        try {
          const reefData: any[] = [];

          // 1. Fetch all properties to check root details
          const propertiesSnapshot = await this.fbDb.collection('properties').get().toPromise();

          // 2. Fetch all sub-certifications efficiently using Collection Group
          const subCertsSnapshot = await this.fbDb.collectionGroup('reefcertified').get().toPromise();
          const subCertsMap: { [key: string]: any[] } = {};

          subCertsSnapshot.docs.forEach(doc => {
            // Parent of subcollection is the Property Doc
            const propertyId = doc.ref.parent.parent?.id;
            if (propertyId) {
              if (!subCertsMap[propertyId]) {
                subCertsMap[propertyId] = [];
              }
              subCertsMap[propertyId].push(doc.data());
            }
          });

          // 3. Process properties
          for (const doc of propertiesSnapshot.docs) {
            const prop = doc.data() as any;
            const propertyId = doc.id;
            const rootDate = prop.reefCertDate;
            const subCerts = subCertsMap[propertyId] || [];

            // Optimisation: Skip if no cert data at all
            if (!rootDate && subCerts.length === 0) continue;

            // Resolve property common details
            const summary = this.propertyService.getPropertySummary(propertyId);
            // Fallbacks if summary not loaded
            const growerId = summary?.growerId || prop.growerId || 'UNKNOWN';
            const company = prop.growerName;
            const propertyName = summary?.name || prop.propertyName || 'UNKNOWN';
            const catchment = summary?.catchment || prop.catchment || 'UNKNOWN';
            const area = summary?.area || prop.area || 'UNKNOWN';
            const latLong = prop.latLong || '';
            const latLongAddress = prop.latLongAddress || '';
            const address = prop.address1 || 'UNKNOWN';
            const suburb = prop.suburb || 'UNKNOWN';
            const state = states?.find(s => s.id === prop?.state)?.name || 'UNKNOWN';
            const postcode = prop.postcode || 'UNKNOWN';
            const crops = prop.crops || [];

            // Base object for this property
            const baseRow = {
              growerID: growerId,
              company: company,
              propertyID: propertyId,
              propertyName: propertyName,
              address_1: address,
              suburb: suburb,
              state: state,
              postcode: postcode,
              catchment: catchment,
              area: area,
              latLong: latLong,
              latLongAddress: latLongAddress,
              crops: crops.map(c => {
                if (c.name){
                  return c.name;
                }
              }).join(', '),
            };

            // A. Add Root Date Row (Source: Main Property Record)
            if (rootDate) {
              reefData.push({
                ...baseRow,
                reefCertDate: rootDate,
                type: 'Certification'
              });
            }

            // B. Add Sub-cert Row(s) (Source: Re-certification collection)
            subCerts.forEach(sub => {
              reefData.push({
                ...baseRow,
                reefCertDate: sub.date,
                type: 'Re-Certification'
              });
            });
          }

          if (reefData.length > 0) {
            const ws = XLSX.utils.json_to_sheet(reefData);
            XLSX.utils.book_append_sheet(wb, ws, 'Reef Certificates');
          }

          resolve();
        } catch (e) {
          console.error(e);
          reject(e.message);
        }
      });

      try {
        await loadReefCertificates;
        const result = XLSX.writeFile(wb, `Results-${moment().format('X')}.xlsx`); // Save to file
      } catch (e) {
        await this.rsDialogService.showErrorDialog('Something went wrong. ' + (e.message ? e.message : e));
      }
      this.rsLoadingModalService.hide();
      return;
    }

    // Generate workbook and add the worksheet

    // Tab 1 - Contacts
    // Tab 3+ - Practice Change
    let contactInfo = []; // TODO: growerId, propertyId, date, type?
    const loadContacts = new Promise<void>((resolve, reject) => {
      try {
        const practiceChangeForms: {[key: string]: any[]} = {};
        Object.keys(this.practiceChangeService.types).forEach(k => practiceChangeForms[k] = []);

        contactInfo = this.reportResults.contacts.map(c => {
          let property = this.propertyService.getPropertySummary(c.property);
          if (!property) {
            property = {id: c.property} as any as Hort360PropertySummary;
          }

          // Load activities
          const activityValues = {};
          Object.keys(c.activities).filter(a => c.activities[a] > 0).map(a => activities.find(aa => aa.id === a))?.forEach(aa => {
            activityValues[this.resolveCategory.transform(aa.category)?.name + ' - ' + aa.name] = c.activities[aa.id];
          });
          // Load practice change entries
          const practiceChange = Object.keys(this.practiceChangeService.types)
            .filter(k => !!c[this.practiceChangeService.types[k].fieldId]?.completed);
          practiceChange.forEach(pct => {
            const pcType = this.resolveCategory.transform(pct);
            if (!pcType) {
              return;
            }
            activityValues[pcType.name + ' - Practice Change'] = 1;

            // Prepare the Practice change output;
            const pcData = c[this.practiceChangeService.types[pct].fieldId];
            const pc = new this.practiceChangeService.types[pct].type({});
            pc.fromObject(pcData);
            const pcOutput = {
              ...property,
              ...pc.forReporting()
            };
            practiceChangeForms[pcType.id].push(pcOutput);
          });

          return {
            id: c.id,
            date: moment(c.date, 'X').format('DD/MM/YYYY'),
            user: knownUsers.find(ku => ku.uid + '' === c.user + '')?.email || 'UNKNOWN',
            method: c.type,
            propertyId: property?.id || 'UNKNOWN',
            growerId: property?.growerId || 'UNKNOWN',
            propertyAddress: property?.address || 'UNKNOWN',
            propertyCatchment: property?.catchment || 'UNKNOWN',
            propertyArea: property?.area || 'UNKNOWN',
            ...activityValues,
          };
        }).filter(v => !!v);

        console.log(contactInfo);
        const ws = XLSX.utils.json_to_sheet(contactInfo);
        XLSX.utils.book_append_sheet(wb, ws, 'Contacts');

        // Build Practice Change
        Object.keys(practiceChangeForms).forEach(pct => {
          const forms = practiceChangeForms[pct];
          const category = this.resolveCategory.transform(pct);
          const wspc = XLSX.utils.json_to_sheet(forms);
          XLSX.utils.book_append_sheet(wb, wspc, ('Practice Change - ' + category.name).substr(0, 30));
        });

        // Done!
        resolve();
      } catch (e) {
        console.error(e);
        reject(e.message);
      }
    });

    // Tab 2 - Events
    const attendeeInfo = [];
    const eventInfo = [];
    const loadEvents = new Promise<void>(async (resolve, reject) => {
      try {
        for (let e = 0; e < this.reportResults.events.length; e++) {
          const event = this.reportResults.events[e];
          const eventData = {
            id: event.id,
            date: moment(event.date, 'X').format('DD/MM/YYYY'),
            user: knownUsers.find(ku => ku.uid + '' === event.user + '')?.email || 'UNKNOWN',
            type: this.resolveCategory.transform(event.category)?.name || 'UNKNOWN',
            name: event.name,
            attendees: event.attendees,
            totalAttendees: event.totalAttendees,
            properties: event.properties,
            locationTown: event.locationTown,
            locationLga: this.resolveLga.transform(event.locationLga)?.name || 'UNKNOWN',
          };
          eventInfo.push(eventData);

          // Load the attending users;
          const attendees = await this.fbDb.collection(`${this.dataService.eventsRef.ref.path}/${event.id}/attendees`).get().toPromise();
          attendees.docs.forEach(a => {
            const property = this.propertyService.getPropertySummary(a.id);

            attendeeInfo.push({
              ...eventData,
              propertyAttendees: (a.data() || {} as any).total || 0,
              propertyId: property?.id || a.id,
              growerId: property?.growerId || 'UNKNOWN',
              propertyAddress: property?.address || 'UNKNOWN',
              propertyCatchment: property?.catchment || 'UNKNOWN',
              propertyArea: property?.area || 'UNKNOWN',
            });
          });
        }
        console.log(eventInfo);
        let ws = XLSX.utils.json_to_sheet(eventInfo);
        XLSX.utils.book_append_sheet(wb, ws, 'Events');

        console.log(attendeeInfo);
        ws = XLSX.utils.json_to_sheet(attendeeInfo);
        XLSX.utils.book_append_sheet(wb, ws, 'Attendees');

        // Done!
        resolve();
      } catch (e) {
        console.error(e);
        reject(e.message);
      }
    });

    //meetings
    const meetingInfo = [];
    const meetingAttendeeInfo = [];
    const loadMeetings = new Promise<void>(async (resolve, reject) => {
      try{
        for (let m = 0; m < this.reportResults.meetings.length; m++) {
          const meeting = this.reportResults.meetings[m];
          const meetingData = {
            id: meeting.id,
            date:  moment(meeting.date, 'X').format('DD/MM/YYYY'),
            topic: meeting.topic,
            locationTown: meeting.locationTown,
            locationLga: this.resolveLga.transform(meeting.locationLga)?.name || 'UNKNOWN',
            notes: meeting.notes,
            attendees: meeting.attendees,
            properties: meeting.properties,
            user:  knownUsers.find(ku => ku.uid + '' === meeting.user + '')?.email || 'UNKNOWN'
          };
          meetingInfo.push(meetingData);

          const attendees = await this.fbDb.collection(`${this.dataService.meetingsRef.ref.path}/${meeting.id}/attendees`).get().toPromise();
          attendees.docs.forEach(a => {

            const property = this.propertyService.getPropertySummary(a.id);
            console.log(property);
            meetingAttendeeInfo.push({
              ...meetingData,
              propertyAttendees: (a.data() || {} as any).total || 0,
              propertyId: property?.id || a.id,
              growerId: property?.growerId || 'UNKNOWN',
              propertyAddress: property?.address || 'UNKNOWN',
              propertyCatchment: property?.catchment || 'UNKNOWN',
              propertyArea: property?.area || 'UNKNOWN',
              name: property?.name || 'UNKNOWN',
              role: property?.role || 'UNKNOWN',
              organisation: property?.growerDescription || 'UNKNOWN',
            });
          });
        }

        let ws = XLSX.utils.json_to_sheet(meetingInfo);
        XLSX.utils.book_append_sheet(wb, ws, 'Meetings');

        ws = XLSX.utils.json_to_sheet(meetingAttendeeInfo);
        XLSX.utils.book_append_sheet(wb, ws, 'Meeting Attendees');

        resolve();
      }catch(e){
        reject(e.message);
      }
    })

    //Assistance
    let assistanceInfo = [];
    const loadAssistance = new Promise<void>(async (resolve, reject) => {
      try{
        assistanceInfo = this.reportResults.assistanceProvided.map(ap => {
          let property = this.propertyService.getPropertySummary(ap.property);
          if (!property) {
            property = {id: ap.property} as any as Hort360PropertySummary;
          }

          // Load activities
          const activityValues = {};
          Object.keys(ap.activities).filter(a => ap.activities[a] > 0).map(a => activities.find(aa => aa.id === a))?.forEach(aa => {
            activityValues[this.resolveCategory.transform(aa.category)?.name + ' - ' + aa.name] = ap.activities[aa.id];
          });

          return {
            id: ap.id,
            date: moment(ap.date, 'X').format('DD/MM/YYYY'),
            user: knownUsers.find(ku => ku.uid + '' === ap.user + '')?.email || 'UNKNOWN',
            propertyId: property?.id || 'UNKNOWN',
            growerId: property?.growerId || 'UNKNOWN',
            propertyAddress: property?.address || 'UNKNOWN',
            crops: property?.propertyCrops?.map(pc=>pc.name).join(",") || 'UNKNOWN',
            area: property?.area || 'UNKNOWN',
            assistanceProvided: this.resolveAssistanceType.transform(ap.type)?.name || 'UNKNOWN',
            riskAddressed: this.resolveRiskType.transform(ap.riskAddressed)?.name || 'UNKNOWN',
            planActivity: this.dataService.localDefinitions.getValue().actionPlans.find(action => action.id === ap.planActivity)?.name || 'UNKNOWN',
            learntNewSkills: ap.learntNewSkills,
            intentToChange: ap.intentToChange,
            practiceChange: ap.practiceChange,
            appliedForGrant: ap.grantAppliedFor,
            grantName: ap.grantAppliedForName,
            referral: this.dataService.localDefinitions.getValue().additionalSupport.find(as => as.id === ap.referralForAdditionalSupport)?.name || 'UNKNOWN',
            followup: ap.flagged,
            ...activityValues,
          };
        }).filter(v => !!v);

        console.log(assistanceInfo);
        const ws = XLSX.utils.json_to_sheet(assistanceInfo);
        XLSX.utils.book_append_sheet(wb, ws, 'Assistance');

        resolve();
      }catch(e){
        reject(e.message);
      }
    })

    //Assistance
    let practiceChangeInfo = [];
    const loadPracticeChange = new Promise<void>(async (resolve, reject) => {
      try{
        practiceChangeInfo = this.reportResults.practiceChanges.map(ap => {
          let property = this.propertyService.getPropertySummary(ap.property);
          if (!property) {
            property = {id: ap.property} as any as Hort360PropertySummary;
          }

          return {
            id: ap.id,
            date: moment(ap.date, 'X').format('DD/MM/YYYY'),
            user: knownUsers.find(ku => ku.uid + '' === ap.user + '')?.email || 'UNKNOWN',
            propertyId: property?.id || 'UNKNOWN',
            growerId: property?.growerId || 'UNKNOWN',
            propertyAddress: property?.address || 'UNKNOWN',
            crops: property?.propertyCrops?.map(pc=>pc.name).join(",") || 'UNKNOWN',
            area: property?.area || 'UNKNOWN',
            notes: ap.notes,
            activity: this.resolvePracticeChangeActivity.transform(ap.activity)?.name || 'UNKNOWN',
          };
        }).filter(v => !!v);

        const ws = XLSX.utils.json_to_sheet(practiceChangeInfo);
        XLSX.utils.book_append_sheet(wb, ws, 'Practice Change');

        resolve();
      }catch(e){
        reject(e.message);
      }
    })

    const loadGrowerReporting = () => new Promise<void>(async (resolve, reject) => {
      try {
        const uniqueCatchments = attendeeInfo.map(a => a.propertyCatchment)
          .concat(contactInfo.map(c => c.propertyCatchment))
          .filter((v, i, a) => v && a.indexOf(v) === i);
        console.log(uniqueCatchments);

        const results = uniqueCatchments.map(c => { return { catchment: c, attendance: {}, contacts: {} }; });

        // Load all contacts;
        for (const contact of contactInfo) {
          const growerId = contact.growerId + '';
          const propertyCatchment = contact.propertyCatchment;
          const date = contact.date;
          const record = results.find(r => r.catchment === propertyCatchment);
          if (!record.contacts.hasOwnProperty(date)) {
            record.contacts[date] = [];
          }
          const idx = record.contacts[date].findIndex(id => id === growerId);
          if (idx === -1) {
            record.contacts[date].push(growerId); // Add this grower to this date
          }
        }

        // Load all attendance;
        for (const attendance of attendeeInfo) {
          const growerId = attendance.growerId + '';
          const propertyCatchment = attendance.propertyCatchment;
          const date = attendance.date;
          const record = results.find(r => r.catchment === propertyCatchment);
          if (!record.attendance.hasOwnProperty(date)) {
            record.attendance[date] = [];
          }
          const idx = record.attendance[date].findIndex(id => id === growerId);
          if (idx === -1) {
            record.attendance[date].push(growerId); // Add this grower to this date
          }
        }

        // Build Raw Results;
        const resultsRaw = [];
        results.forEach(r => {
          Object.keys(r.contacts).forEach(date => {
            resultsRaw.push({
              catchment: r.catchment,
              date,
              type: 'contact',
              growers: r.contacts[date].join(',')
            });
          });
          Object.keys(r.attendance).forEach(date => {
            resultsRaw.push({
              catchment: r.catchment,
              date,
              type: 'attendance',
              growers: r.attendance[date].join(',')
            });
          });
        });
        let ws = XLSX.utils.json_to_sheet(resultsRaw);
        XLSX.utils.book_append_sheet(wb, ws, 'Grower Catchments - Raw'.substr(0, 30));


        const resultSummary = results.map(r => {
          return {
            catchment: r.catchment,
            attendance: Object.keys(r.attendance).map(k => r.attendance[k].length).reduce((p, c, i) => p + c, 0),
            contact: Object.keys(r.contacts).map(k => r.contacts[k].length).reduce((p, c, i) => p + c, 0)
          };
        });
        ws = XLSX.utils.json_to_sheet(resultSummary);
        XLSX.utils.book_append_sheet(wb, ws, 'Grower Catchments - Summary'.substr(0, 30));


        // Done!
        resolve();
      } catch (e) {
        console.error(e);
        reject(e.message);
      }
    });



    // Wait for the results;
    try {
        await Promise.all([loadContacts, loadEvents, loadMeetings, loadAssistance, loadPracticeChange]);
        // Finally, build summary for Grower level reporting;
        await loadGrowerReporting();

      const result = XLSX.writeFile(wb, `Results-${moment().format('X')}.xlsx`); // Save to file
    } catch (e) {
      this.rsDialogService.showErrorDialog('Something went wrong. ' + (e.message ? e.message : e));
    }
    this.rsLoadingModalService.hide();
  }

  downloadReport() {
    // Date, User, Category              , Category Total
    //             Activity 1, Activity 2

  }

  invertColor(hex) {
    return invert(hex || '#ffffff', true);
  }
}

enum ReportingType {
  INTERACTIONS_BY_CATEGORY ,
  INTERACTIONS_PER_USER ,
  CONTACT_EVENTS,
  ALL_ACTIVITIES,
  MEETING_WITH_OTHERS,
  ASSISTANCE_REPORT,
  PRACTICE_CHANGE_REPORT,
  REEF_CERTIFICATE_REPORT,
}
